import { useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Share } from "lucide-react"

import { useNotebookSharing } from "../hooks/useNotebookSharing"
import {
  notebookSharePath,
  SHARING_AUDIENCES,
  type NotebookSharingScope,
  type SharingAudience,
} from "../utils/notebookSharing"

const LABELS: Record<SharingAudience, string> = {
  private: "Only me",
  workspace: "This workspace",
  organization: "This organization",
  public: "Anyone with the link",
}

export function NotebookShareButton(scope: NotebookSharingScope) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<SharingAudience | null>(null)
  const [copyMessage, setCopyMessage] = useState<string | null>(null)
  const { sharing, pending, error, save } = useNotebookSharing(scope, open)
  const selected = draft ?? sharing?.audience ?? "private"
  const changed = !!sharing && selected !== sharing.audience
  const path = sharing ? notebookSharePath(scope, sharing) : null
  const copy = async () => {
    if (!path) {
      return
    }
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`)
      setCopyMessage("Link copied")
    } catch {
      setCopyMessage("Could not copy the link. Please try again.")
    }
  }
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setDraft(null)
          setCopyMessage(null)
          setOpen(true)
        }}
      >
        <Share className="size-4" /> Share
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share notebook</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="text-sm text-coval-text-muted">
              Choose who can read this saved notebook using its link. Only you
              can rename, delete, or change sharing.
            </p>
            <Select
              value={selected}
              onValueChange={(value) => {
                setDraft(value as SharingAudience)
                setCopyMessage(null)
              }}
              disabled={pending || !sharing}
            >
              <SelectTrigger aria-label="Who can access this notebook">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHARING_AUDIENCES.map((audience) => (
                  <SelectItem key={audience} value={audience}>
                    {LABELS[audience]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected === "public" ? (
              <p className="text-sm text-coval-text">
                Anyone with the link can read the saved text, tables, and charts
                without signing in. Check the content before making it public.
              </p>
            ) : null}
            {selected === "workspace" || selected === "organization" ? (
              <p className="text-sm text-coval-text">
                Recipients must sign in and have access to this {selected}. Send
                them the link; it does not add the notebook to their personal
                list.
              </p>
            ) : null}
            <p className="text-sm text-coval-text-muted">
              Linked traces and simulations keep their own permissions. Changing
              access replaces the link; choosing Only me revokes it.
            </p>
            {error ? (
              <p role="alert" className="text-sm text-coval-danger-text">
                {error}
              </p>
            ) : null}
            {path && !changed && open && (
              <label className="block space-y-2 text-sm">
                Share link
                <Input
                  aria-label="Share link"
                  readOnly
                  value={`${window.location.origin}${path}`}
                  onFocus={(event) => event.target.select()}
                />
              </label>
            )}
            {copyMessage ? (
              <p role="status" className="text-sm text-coval-text">
                {copyMessage}
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Close
            </Button>
            {changed ? (
              <Button
                disabled={pending}
                onClick={() =>
                  void save(selected).then((ok) => {
                    if (ok) {
                      setDraft(null)
                      setCopyMessage(null)
                    }
                  })
                }
              >
                {pending
                  ? "Saving…"
                  : selected === "public"
                    ? "Make public"
                    : "Save access"}
              </Button>
            ) : (
              <Button
                disabled={!path || pending || !!error}
                onClick={() => void copy()}
              >
                Copy link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
